import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { AGENT_SETTINGS } from "@/constants/testIds";
import AgentConfigTab from "@/components/agent/AgentConfigTab";
import KnowledgeBaseTab from "@/components/agent/KnowledgeBaseTab";
import RulesTab from "@/components/agent/RulesTab";
import SchedulingTab from "@/components/agent/SchedulingTab";
import PlaygroundTab from "@/components/agent/PlaygroundTab";
import MetricsTab from "@/components/agent/MetricsTab";
import TrainingTab from "@/components/agent/TrainingTab";
import SmsTab from "@/components/agent/SmsTab";
import ConversationsPanel from "@/components/conversations/ConversationsPanel";

/** Panel completo de administracion del agente de IA de WhatsApp:
 * Configuracion, Base de conocimiento, Reglas, Probar, Conversaciones y
 * Metricas. Admin-only (Recepcion usa la pagina standalone /conversaciones). */
export default function AgentSettingsPage() {
  const [config, setConfig] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const { toast } = useToast();

  const loadAll = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [configRes, knowledgeRes, faqsRes, documentsRes] = await Promise.all([
        api.get("/agent/config"),
        api.get("/agent/knowledge"),
        api.get("/agent/faqs"),
        api.get("/agent/documents"),
      ]);
      setConfig(configRes.data);
      setKnowledge(knowledgeRes.data);
      setFaqs(faqsRes.data);
      setDocuments(documentsRes.data);
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la configuracion del agente.";
      setLoadError(message);
      toast({ title: "Error al cargar la configuracion del agente", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSaveConfig = async (payload) => {
    try {
      const { data } = await api.put("/agent/config", payload);
      setConfig((prev) => ({ ...prev, ...data }));
      toast({ title: "Configuracion guardada" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleUpdateKnowledge = async (serviceId, form) => {
    await api.put(`/agent/knowledge/${serviceId}`, form);
    toast({ title: "Informacion del tratamiento actualizada" });
    loadAll();
  };

  const handleCreateFaq = async (form) => {
    await api.post("/agent/faqs", form);
    toast({ title: "Pregunta frecuente creada" });
    loadAll();
  };

  const handleUpdateFaq = async (id, form) => {
    await api.put(`/agent/faqs/${id}`, form);
    toast({ title: "Pregunta frecuente actualizada" });
    loadAll();
  };

  const handleDeleteFaq = async (id) => {
    try {
      await api.delete(`/agent/faqs/${id}`);
      toast({ title: "Pregunta frecuente eliminada" });
      loadAll();
    } catch (err) {
      toast({ title: "Error al eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleUploadDocument = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post("/agent/documents", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast({ title: "Documento subido", description: "El agente ya puede usar su contenido para responder." });
      loadAll();
    } catch (err) {
      toast({ title: "No se pudo subir el documento", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDeleteDocument = async (id) => {
    try {
      await api.delete(`/agent/documents/${id}`);
      toast({ title: "Documento eliminado" });
      loadAll();
    } catch (err) {
      toast({ title: "Error al eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const configTabsBlocked = !loading && !config;

  return (
    <div className="space-y-6" data-testid={AGENT_SETTINGS.page}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Agente de IA</h1>
        <p className="text-base text-muted-foreground mt-1">Configuracion, knowledge base, reglas, simulador, conversaciones y metricas del agente de WhatsApp.</p>
      </div>

      {loadError && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={AGENT_SETTINGS.loadErrorBanner}>
          <p className="text-sm text-destructive">{loadError}</p>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid={AGENT_SETTINGS.loadErrorRetryButton}>
            {loading ? "Reintentando..." : "Reintentar"}
          </Button>
        </div>
      )}

      <Tabs defaultValue="config" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="config" data-testid={AGENT_SETTINGS.tabConfig}>Configuracion</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid={AGENT_SETTINGS.tabKnowledge}>Base de conocimiento</TabsTrigger>
          <TabsTrigger value="rules" data-testid={AGENT_SETTINGS.tabRules}>Reglas</TabsTrigger>
          <TabsTrigger value="scheduling" data-testid={AGENT_SETTINGS.tabScheduling}>Agendamiento</TabsTrigger>
          <TabsTrigger value="playground" data-testid={AGENT_SETTINGS.tabPlayground}>Probar</TabsTrigger>
          <TabsTrigger value="training" data-testid={AGENT_SETTINGS.tabTraining}>Entrenamiento</TabsTrigger>
          <TabsTrigger value="sms" data-testid={AGENT_SETTINGS.tabSms}>SMS</TabsTrigger>
          <TabsTrigger value="conversations" data-testid={AGENT_SETTINGS.tabConversations}>Conversaciones</TabsTrigger>
          <TabsTrigger value="metrics" data-testid={AGENT_SETTINGS.tabMetrics}>Metricas</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="pt-6">
          {loading ? <p className="text-sm text-muted-foreground">Cargando...</p> : configTabsBlocked ? null : <AgentConfigTab config={config} onSave={handleSaveConfig} />}
        </TabsContent>
        <TabsContent value="knowledge" className="pt-6">
          <KnowledgeBaseTab
            documents={documents} knowledge={knowledge} faqs={faqs} loading={loading}
            onUploadDocument={handleUploadDocument} onDeleteDocument={handleDeleteDocument}
            onUpdateKnowledge={handleUpdateKnowledge} onCreateFaq={handleCreateFaq}
            onUpdateFaq={handleUpdateFaq} onDeleteFaq={handleDeleteFaq}
          />
        </TabsContent>
        <TabsContent value="rules" className="pt-6">
          {loading ? <p className="text-sm text-muted-foreground">Cargando...</p> : configTabsBlocked ? null : <RulesTab config={config} onSave={handleSaveConfig} />}
        </TabsContent>
        <TabsContent value="scheduling" className="pt-6">
          {loading ? <p className="text-sm text-muted-foreground">Cargando...</p> : configTabsBlocked ? null : <SchedulingTab config={config} onSave={handleSaveConfig} />}
        </TabsContent>
        <TabsContent value="playground" className="pt-6">
          <PlaygroundTab />
        </TabsContent>
        <TabsContent value="training" className="pt-6">
          {loading ? <p className="text-sm text-muted-foreground">Cargando...</p> : configTabsBlocked ? null : <TrainingTab config={config} onSave={handleSaveConfig} />}
        </TabsContent>
        <TabsContent value="sms" className="pt-6">
          <SmsTab />
        </TabsContent>
        <TabsContent value="conversations" className="pt-6">
          {/* Fix AI (2026-10-23): alto visible real en el celular */}
          <ConversationsPanel heightClass="h-[75dvh]" />
        </TabsContent>
        <TabsContent value="metrics" className="pt-6">
          <MetricsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
